<template>
  <div
    v-if="isDemoActive"
    class="interactive-demo"
  >
    <!-- Overlay with highlight -->
    <DemoOverlay
      :target-element="currentTargetElement"
      :position="currentStep.position"
      :padding="HIGHLIGHT.PADDING"
      :content="currentStep.instruction"
      :show="showOverlay"
      :close-on-backdrop-click="false"
      @close="handleClose"
    />

    <!-- Demo container with step, controls, and progress -->
    <div
      class="interactive-demo__container"
      :class="demoContainerClasses"
    >
      <!-- Step content -->
      <div class="interactive-demo__step-wrapper">
        <DemoStep
          :step="currentStep"
          :current-step-index="currentStepIndex"
          :total-steps="totalSteps"
        />
      </div>

      <!-- Progress bar -->
      <DemoProgressBar
        :current-step="currentStepIndex"
        :total-steps="totalSteps"
        :completed-steps="completedSteps"
        :show-step-indicators="true"
        :allow-step-navigation="false"
        :show-percentage="true"
        :animated="true"
      />

      <!-- Controls -->
      <DemoControls
        :can-go-back="!isFirstStep"
        :can-go-next="!isLastStep"
        :current-step="currentStepIndex"
        :total-steps="totalSteps"
        :show-back-button="true"
        :show-close-button="!isLastStep"
        :next-button-ready="nextButtonReady"
        @back="handlePreviousStep"
        @next="handleNextStep"
        @close="handleClose"
        @finish="handleFinish"
      />

      <!-- Completion screen (shown on last step after completion) -->
      <div
        v-if="showCompletionScreen"
        class="interactive-demo__completion"
      >
        <div class="interactive-demo__completion-content">
          <div class="interactive-demo__completion-icon">
            🎉
          </div>
          <h2 class="interactive-demo__completion-title">
            Congratulations!
          </h2>
          <p class="interactive-demo__completion-message">
            You've completed the interactive demo and learned how to use all the key features.
          </p>
          <p class="interactive-demo__completion-stats">
            Demo Duration: {{ demoDurationFormatted }} | Steps Completed: {{ completedSteps.length }}/{{ totalSteps }}
          </p>
          <div class="interactive-demo__completion-actions">
            <button
              class="interactive-demo__completion-btn interactive-demo__completion-btn--finish"
              @click="handleFinish"
            >
              Finish Demo
            </button>
            <button
              v-if="allowDemoRestart"
              class="interactive-demo__completion-btn interactive-demo__completion-btn--restart"
              @click="handleRestart"
            >
              Restart Demo
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Navigation-away confirmation dialog -->
    <div style="pointer-events: auto;">
      <DemoConfirmationDialog
        :visible="navConfirmVisible"
        message-type="navigation-warning"
        :show-cancel-button="false"
        @continue="handleNavConfirmContinue"
        @exit="handleNavConfirmExit"
        @close="handleNavConfirmContinue"
        @update:visible="navConfirmVisible = $event"
      />
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import DemoStep from './DemoStep.vue';
import DemoOverlay from './DemoOverlay.vue';
import DemoControls from './DemoControls.vue';
import DemoProgressBar from './DemoProgressBar.vue';
import DemoConfirmationDialog from './DemoConfirmationDialog.vue';
import { getTargetElement, waitForElement, isElementVisible, scrollToElement, injectDemoAnimations, cleanupDemo, getDemoSynchronizationService } from '@/demo/services';
import { getDemoSimulationService } from '@/demo/services';
import { DEMO_STEPS, HIGHLIGHT, DEMO_BEHAVIOR, DEMO_STATUS } from '@/demo/constants';

/**
 * InteractiveDemo Component
 * Main orchestrator component that manages the complete demo flow
 * Coordinates all sub-components and handles user interactions
 * 
 * @component
 * @example
 * <InteractiveDemo />
 */
export default {
  name: 'InteractiveDemo',

  components: {
    DemoStep,
    DemoOverlay,
    DemoControls,
    DemoProgressBar,
    DemoConfirmationDialog,
  },

  props: {
    /**
     * Whether to show demo on mount
     */
    autoStart: {
      type: Boolean,
      default: false,
    },

    /**
     * Allow demo to be restarted
     */
    allowDemoRestart: {
      type: Boolean,
      default: true,
    },

    /**
     * Show completion screen
     */
    showCompletionScreenOnFinish: {
      type: Boolean,
      default: true,
    },

    /**
     * Auto-wait for target elements
     */
    autoWaitForElements: {
      type: Boolean,
      default: true,
    },

    /**
     * Timeout for waiting for elements (milliseconds)
     */
    elementWaitTimeout: {
      type: Number,
      default: DEMO_BEHAVIOR.ELEMENT_WAIT_TIMEOUT,
    },
  },

  emits: {
    /**
     * Emitted when demo starts
     */
    'demo-start': null,

    /**
     * Emitted when demo ends/completes
     */
    'demo-end': null,

    /**
     * Emitted when demo is closed
     */
    'demo-close': null,

    /**
     * Emitted when step changes
     * Payload: { stepIndex, step }
     */
    'step-change': null,

    /**
     * Emitted when an error occurs
     */
    'error': null,
  },

  data() {
    return {
      HIGHLIGHT,
      showOverlay: true,
      currentTargetElement: null,
      showError: false,
      errorMessage: '',
      errorRetryCount: 0,
      maxErrorRetries: 3,
      showCompletionScreen: false,
      elementFindAttempts: 0,
      resizeObserver: null,
      resizeObserverTimeout: null,
      mutationObserver: null,
      targetClickElement: null,
      navConfirmVisible: false,
      blockedNavDestination: null,
      demoIsNavigating: false,
      routeGuardUnsubscribe: null,
    };
  },

  computed: {
    /**
     * Vuex store state
     */
    ...mapState('demo', {
      demoStatus: 'status',
      isActive: 'isActive',
      currentStepIndex: 'currentStep',
      completedSteps: 'completedSteps',
      demoStartTime: 'demoStartTime',
      demoEndTime: 'demoEndTime',
    }),

    /**
     * Vuex getters
     */
    ...mapGetters('demo', [
      'currentStepData',
      'totalSteps',
      'isDemoActive',
      'isLastStep',
      'isFirstStep',
      'demoDuration',
      'demoProgress',
    ]),

    /**
     * Current step object
     */
    currentStep() {
      return DEMO_STEPS[this.currentStepIndex] || {};
    },

    /**
     * Current step number (1-based)
     */
    currentStepNumber() {
      return this.currentStepIndex + 1;
    },

    /**
     * CSS classes for main demo container
     */
    demoContainerClasses() {
      return {
        'interactive-demo__container--mobile': this.isMobileViewport,
        'interactive-demo__container--final-step': this.isLastStep,
        'interactive-demo__container--error': this.showError,
      };
    },

    /**
     * Format demo duration for display
     */
    demoDurationFormatted() {
      const seconds = this.demoDuration;
      const minutes = Math.floor(seconds / 60);
      const secs = seconds % 60;

      if (minutes === 0) {
        return `${secs}s`;
      }
      return `${minutes}m ${secs}s`;
    },

    /**
     * Whether viewport is mobile
     */
    isMobileViewport() {
      return typeof window !== 'undefined' && window.innerWidth <= 768;
    },

    /**
     * Next button should be enabled only when the current step's target element
     * has been found and is in the DOM. Steps without a target are always ready.
     */
    nextButtonReady() {
      return true;
    },

    /**
     * Check if demo is active
     */
    isDemoActive() {
      return this.isActive;
    },
  },

  watch: {
    /**
     * Watch current step changes
     */
    currentStepIndex(newVal, oldVal) {
      if (newVal !== oldVal) {
        console.log('[InteractiveDemo] 👀 currentStepIndex watcher fired: step', oldVal, '→', newVal)
        console.log('[InteractiveDemo] 🔍 New step:', this.currentStep?.id || 'unknown')
        this.onStepChange();
      }
    },

    /**
     * Watch for completion
     */
    isLastStep(newVal) {
      if (newVal && this.isActive) {
        console.log('[InteractiveDemo] 🎯 Last step reached, marking as complete')
        this.markCurrentStepAsComplete();
      }
    },
  },

  mounted() {
    // Inject demo animations
    injectDemoAnimations();

    // Setup event listeners
    window.addEventListener('resize', this.handleWindowResize);
    document.addEventListener('keydown', this.handleKeydown);

    // Auto-start if prop is true
    if (this.autoStart) {
      this.startDemo();
      this.$emit('demo-start');
    }
  },

  beforeUnmount() {
    // Cleanup
    this.cleanup();
  },

  methods: {
    /**
     * Vuex action bindings
     */
    ...mapActions('demo', [
      'startDemo',
      'completeDemo',
      'closeDemo',
      'nextStep',
      'previousStep',
      'resetDemo',
    ]),

    /**
     * Start demo
     */
    async start() {
      // Initialize synchronization service to enable automatic step advancement
      console.log('[InteractiveDemo] 🚀 Starting demo...')
      const syncService = getDemoSynchronizationService();
      console.log('[InteractiveDemo] 🔧 Initializing sync service...')
      await syncService.initialize(DEMO_STEPS, 0, this.$router, this.$store);
      console.log('[InteractiveDemo] ✅ Sync service initialized')

      // Install a route guard that shows the confirmation dialog when the user
      // tries to navigate away from the current step's page to an unexpected page.
      this.routeGuardUnsubscribe = this.$router.beforeEach((to, from, next) => {
        if (!this.isDemoActive || this.demoIsNavigating) {
          next();
          return;
        }
        const stepRoute = this.currentStep?.pageRoute;
        if (stepRoute && to.path !== stepRoute) {
          // Allow navigation to the next step's route silently.
          // This handles the case where the highlighted element is a router-link:
          // Vue Router fires the navigation before our handleTargetElementClick
          // listener has a chance to set demoIsNavigating = true.
          const nextStepRoute = DEMO_STEPS[this.currentStepIndex + 1]?.pageRoute;
          if (nextStepRoute && to.path === nextStepRoute) {
            next();
            return;
          }
          this.blockedNavDestination = to.fullPath;
          this.navConfirmVisible = true;
          next(false);
          return;
        }
        next();
      });
      
      console.log('[InteractiveDemo] 📊 Dispatching demo/startDemo to Vuex...')
      this.$store.dispatch('demo/startDemo');
      this.$emit('demo-start');
      console.log('[InteractiveDemo] 🎬 Demo started, calling onStepChange()')
      await this.onStepChange();
    },

    /**
     * Handle step change
     */
    async onStepChange() {
      console.log('[InteractiveDemo] 🔄 onStepChange() called for step', this.currentStepIndex)
      this.showCompletionScreen = false;
      this.showError = false;
      this.errorRetryCount = 0;

      // Hide overlay to prevent showing old and new highlights simultaneously
      console.log('[InteractiveDemo] 👁️ Hiding overlay for transition...')
      this.showOverlay = false;

      // Clear previous highlight immediately
      console.log('[InteractiveDemo] 🗑️ Clearing previous highlight...')
      this.currentTargetElement = null;

      // Wait longer for overlay transition and DOM cleanup
      // This ensures: 
      // - Overlay CSS animation completes (300ms)
      // - Old element bounds are cleared from DemoOverlay component
      // - ResizeObserver and MutationObserver are properly cleaned up
      await new Promise(resolve => setTimeout(resolve, 600));

      // Find target element
      console.log('[InteractiveDemo] 🔎 Updating target element...')
      await this.updateTargetElement();

      // Give Vue reactivity time to update DemoOverlay's targetElement watcher
      // and recalculate element bounds before showing the overlay
      await new Promise(resolve => setTimeout(resolve, 100));

      // Only show overlay if we found a valid target element
      if (this.currentTargetElement) {
        console.log('[InteractiveDemo] 👁️ Showing overlay with new target...')
        this.showOverlay = true;
      } else {
        console.log('[InteractiveDemo] ⚠️ No target element found, overlay stays hidden')
        this.showOverlay = false;
      }

      // Emit event
      console.log('[InteractiveDemo] 📤 Emitting step-change event')
      this.$emit('step-change', {
        stepIndex: this.currentStepIndex,
        step: this.currentStep,
      });
    },

    /**
     * Update target element for current step
     */
    async updateTargetElement() {
      const selector = this.currentStep.targetElement;

      if (!selector) {
        this.currentTargetElement = null;
        return;
      }

      try {
        // Try to find element immediately
        let element = getTargetElement(selector);

        // If not found and autoWait is enabled, wait for it
        if (!element && this.autoWaitForElements) {
          element = await waitForElement(selector, this.elementWaitTimeout);
        }

        if (element) {
          // If waitForVisibility is enabled, wait until element is visible
          if (this.currentStep.waitForVisibility) {
            element = await this.waitForElementVisibility(element, this.elementWaitTimeout);
          }

          if (element) {
            this.currentTargetElement = element;
            this.setupElementObservers(element);
            this.showError = false;
            return;
          }
        }

        // Element not found
        this.showElementNotFoundError(selector);
      } catch (error) {
        this.showElementNotFoundError(selector, error.message);
      }
    },

    /**
     * Wait for element to become visible
     * @param {HTMLElement} element - Element to wait for
     * @param {number} timeout - Maximum time to wait in milliseconds
     * @returns {Promise<HTMLElement|null>} - Resolves with element when visible or null if timeout
     */
    async waitForElementVisibility(element, timeout = this.elementWaitTimeout) {
      return new Promise((resolve) => {
        let settled = false;

        const finish = (el) => {
          if (settled) return;
          settled = true;
          clearInterval(pollId);
          observer.disconnect();
          elementObserver.disconnect();
          resolve(el);
        };

        // Check if already visible
        if (isElementVisible(element)) {
          console.log('[InteractiveDemo] ✅ Element is already visible');
          // Allow browser to complete layout/paint before displaying overlay
          // This ensures overlay renders at correct position after element transitions
          setTimeout(() => {
            resolve(element);
          }, 50);
          return;
        }

        console.log('[InteractiveDemo] ⏳ Element not visible, scrolling into view...');
        
        // Scroll element into view if not visible
        scrollToElement(element).then(() => {
          console.log('[InteractiveDemo] 📜 Scroll completed, checking visibility...');
        });

        // Poll for visibility — handles display:none, v-show, and other CSS visibility changes
        // Run polling more frequently (50ms) for faster detection of API-loaded elements
        const pollId = setInterval(() => {
          if (isElementVisible(element)) {
            console.log('[InteractiveDemo] ✅ Element is now visible (poll)');
            finish(element);
          }
        }, 50);

        // Use MutationObserver on the element itself to catch visibility changes (style, class, v-show)
        const observer = new MutationObserver(() => {
          if (isElementVisible(element)) {
            console.log('[InteractiveDemo] ✅ Element is now visible (document mutation)');
            finish(element);
          }
        });

        observer.observe(document.body, { 
          childList: true, 
          subtree: true, 
          attributes: true, 
          attributeFilter: ['style', 'class', 'hidden', 'data-v-show'] 
        });

        // Also watch the element's own attributes for visibility changes (e.g., when v-show directive updates)
        const elementObserver = new MutationObserver(() => {
          if (isElementVisible(element)) {
            console.log('[InteractiveDemo] ✅ Element is now visible (element mutation)');
            finish(element);
          }
        });

        elementObserver.observe(element, { 
          attributes: true, 
          attributeFilter: ['style', 'class'],
          attributeOldValue: true 
        });

        // Fallback timeout
        setTimeout(() => {
          console.warn('[InteractiveDemo] ⚠️ Element visibility timeout — proceeding anyway');
          finish(element);
        }, timeout);
      });
    },

    /**
     * Setup observers for target element changes
     */
    setupElementObservers(element) {
      if (!element) return;

      // ResizeObserver to track element size changes (debounced to avoid notification loop)
      if (window.ResizeObserver) {
        if (this.resizeObserver) {
          this.resizeObserver.disconnect();
        }

        this.resizeObserver = new ResizeObserver(() => {
          // Debounce the update to prevent ResizeObserver loop
          if (this.resizeObserverTimeout) {
            clearTimeout(this.resizeObserverTimeout);
          }
          this.resizeObserverTimeout = setTimeout(() => {
            this.$forceUpdate();
          }, 50);
        });

        this.resizeObserver.observe(element);
      }

      // Click listener to advance step when user clicks the target element directly
      if (this.targetClickElement) {
        this.targetClickElement.removeEventListener('click', this.handleTargetElementClick);
      }
      this.targetClickElement = element;
      element.addEventListener('click', this.handleTargetElementClick);

      // MutationObserver to track DOM changes
      if (element.parentElement) {
        if (this.mutationObserver) {
          this.mutationObserver.disconnect();
        }

        this.mutationObserver = new MutationObserver(() => {
          // Update position in case element moved
          this.$forceUpdate();
        });

        this.mutationObserver.observe(element.parentElement, {
          childList: true,
          subtree: true,
        });
      }
    },

    /**
     * Handle element not found error
     */
    showElementNotFoundError(selector, details = '') {
      this.showError = true;
      this.errorMessage = `Could not find demo element: ${selector.split(',')[0].trim()}${details ? ` (${details})` : ''}`;
      this.$emit('error', {
        type: 'element-not-found',
        selector,
        details,
      });
    },

    /**
     * Called when the user clicks the highlighted target element directly.
     * Advances the step without re-running simulation (user already performed the action).
     */
    handleTargetElementClick() {
      if (!this.isDemoActive || this.isLastStep) return;
      this.advanceStepAfterInteraction();
    },

    /**
     * Advance to the next step after the user has manually performed the required action.
     * Skips simulation since the action is already done.
     */
    async advanceStepAfterInteraction() {
      this.demoIsNavigating = true;
      try {
        this.markCurrentStepAsComplete();
        const syncService = getDemoSynchronizationService();
        if (syncService && syncService.isActive()) {
          const success = await syncService.advanceToNextStep();
          if (success) {
            await this.onStepChange();
          }
        } else {
          await this.$store.dispatch('demo/nextStep');
          await this.onStepChange();
        }
      } finally {
        this.demoIsNavigating = false;
      }
    },

    /**
     * Handle error retry
     */
    async handleErrorRetry() {
      this.errorRetryCount += 1;

      if (this.errorRetryCount <= this.maxErrorRetries) {
        await this.updateTargetElement();
      } else {
        this.errorMessage = 'Max retries exceeded. Please check the page layout or skip this step.';
      }
    },

    /**
     * Handle error dismiss
     */
    handleErrorDismiss() {
      this.showError = false;
    },

    /**
     * Handle next step
     */
    async handleNextStep() {
      console.log('[InteractiveDemo] ⏭️ handleNextStep() called');

      // Detach the target element click listener BEFORE simulating to prevent
      // the programmatic .click() from double-firing advanceStepAfterInteraction().
      if (this.targetClickElement) {
        this.targetClickElement.removeEventListener('click', this.handleTargetElementClick);
        this.targetClickElement = null;
      }

      this.demoIsNavigating = true;
      try {
        // Mark current step as complete
        this.markCurrentStepAsComplete();

        // Simulate the step action before advancing
        const choice = this.currentStep.simulateAction?.choice;
        await getDemoSimulationService().simulateStepAction(this.currentStep, this.$router, choice);

        // Use DemoSynchronizationService for proper state management
        const syncService = getDemoSynchronizationService();
        if (syncService && syncService.isActive()) {
          console.log('[InteractiveDemo] 🔄 Using DemoSynchronizationService.advanceToNextStep()');
          const success = await syncService.advanceToNextStep();
          if (success) {
            console.log('[InteractiveDemo] ✅ advanceToNextStep() successful, updating overlay...');
            await this.onStepChange();
          } else {
            console.warn('[InteractiveDemo] ⚠️ advanceToNextStep() returned false');
          }
        } else {
          // Fallback to direct Vuex dispatch
          console.log('[InteractiveDemo] 📊 Sync service not active, using Vuex fallback');
          await this.$store.dispatch('demo/nextStep');
          await this.onStepChange();
        }
      } finally {
        this.demoIsNavigating = false;
      }
    },

    /**
     * Handle previous step
     */
    async handlePreviousStep() {
      console.log('[InteractiveDemo] ⏮️ handlePreviousStep() called')
      this.demoIsNavigating = true;
      try {
        // Use DemoSynchronizationService for proper state management
        const syncService = getDemoSynchronizationService();
        if (syncService && syncService.isActive()) {
          console.log('[InteractiveDemo] 🔄 Using DemoSynchronizationService.goToPreviousStep()');
          const success = await syncService.goToPreviousStep();
          if (success) {
            console.log('[InteractiveDemo] ✅ goToPreviousStep() successful, updating overlay...');
            await this.onStepChange();
          } else {
            console.warn('[InteractiveDemo] ⚠️ goToPreviousStep() returned false');
          }
        } else {
          // Fallback to direct Vuex dispatch
          console.log('[InteractiveDemo] 📊 Sync service not active, using Vuex fallback');
          const success = this.$store.dispatch('demo/previousStep');

          if (success) {
            await this.onStepChange();
          }
        }
      } finally {
        this.demoIsNavigating = false;
      }
    },

    /**
     * Mark current step as complete
     */
    markCurrentStepAsComplete() {
      if (!this.completedSteps.includes(this.currentStepIndex)) {
        this.$store.dispatch('demo/markStepComplete', this.currentStepIndex);
      }
    },

    /**
     * Handle finish demo
     */
    handleFinish() {
      // Mark final step as complete
      this.markCurrentStepAsComplete();

      // Complete demo
      this.$store.dispatch('demo/completeDemo');
      this.$emit('demo-end');

      // Cleanup
      cleanupDemo();
      
      // Delay to show completion message
      setTimeout(() => {
        this.cleanup();
      }, 1500);
    },

    /**
     * Handle demo close
     */
    handleClose() {
      if (confirm('Are you sure you want to close the demo? You can restart it later using the "Run Demo" button.')) {
        this.$store.dispatch('demo/closeDemo');
        this.$emit('demo-close');
        cleanupDemo();
        this.cleanup();
      }
    },

    /**
     * Handle demo restart
     */
    handleRestart() {
      this.$store.dispatch('demo/resetDemo');
      this.showCompletionScreen = false;
      this.start();
    },

    /**
     * Handle window resize
     */
    handleWindowResize() {
      this.$forceUpdate();
    },

    /**
     * Handle keyboard events
     */
    handleKeydown(event) {
      if (!this.isDemoActive) return;

      // Only handle if demo container is focused
      const demoElement = this.$el;
      if (!demoElement || !demoElement.contains(document.activeElement)) {
        return;
      }

      switch (event.key) {
        case 'ArrowRight':
        case ' ':
          if (event.ctrlKey || event.metaKey || event.key === ' ') {
            event.preventDefault();
            if (!this.isLastStep) {
              this.handleNextStep();
            }
          }
          break;

        case 'ArrowLeft':
          if (event.ctrlKey || event.metaKey) {
            event.preventDefault();
            if (!this.isFirstStep) {
              this.handlePreviousStep();
            }
          }
          break;

        case 'Escape':
          event.preventDefault();
          this.handleClose();
          break;

        default:
          break;
      }
    },

    /**
     * Handle nav-confirm "Continue Demo" — stay on the current step page.
     */
    handleNavConfirmContinue() {
      this.navConfirmVisible = false;
      this.blockedNavDestination = null;
    },

    /**
     * Handle nav-confirm "Exit & Proceed" — exit the demo and go to the intended destination.
     */
    handleNavConfirmExit() {
      const destination = this.blockedNavDestination;
      this.navConfirmVisible = false;
      this.blockedNavDestination = null;
      this.$store.dispatch('demo/closeDemo');
      this.$emit('demo-close');
      this.cleanup(); // removes the route guard before navigating
      if (destination) {
        this.$router.push(destination);
      }
    },

    /**
     * Cleanup resources
     */
    cleanup() {
      // Remove the route guard installed during start()
      if (this.routeGuardUnsubscribe) {
        this.routeGuardUnsubscribe();
        this.routeGuardUnsubscribe = null;
      }

      // Stop the sync service — this calls routeGuard.stopGuard() which sets
      // DemoRouteGuard.isDemoActive = false, making its global beforeEach a no-op.
      // Without this, the demoRouteGuard's STRICT_ROUTE_VALIDATION guard keeps
      // blocking all non-demo routes after the demo closes.
      try {
        getDemoSynchronizationService().stop();
      } catch (e) {
        // ignore — stop() may throw if already stopped
      }

      // Clear any pending timeouts
      if (this.resizeObserverTimeout) {
        clearTimeout(this.resizeObserverTimeout);
      }

      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
      }

      if (this.mutationObserver) {
        this.mutationObserver.disconnect();
      }

      if (this.targetClickElement) {
        this.targetClickElement.removeEventListener('click', this.handleTargetElementClick);
        this.targetClickElement = null;
      }

      window.removeEventListener('resize', this.handleWindowResize);
      document.removeEventListener('keydown', this.handleKeydown);

      cleanupDemo();
    },
  },
};
</script>

<style scoped lang="scss">
.interactive-demo {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 998;
  pointer-events: none;

  &__container {
    position: fixed;
    bottom: 40px;
    left: 50%;
    transform: translateX(-50%);
    padding: 10px 16px;
    background-color: #1e1e2e;
    border-top: 1px solid #2a2a3e;
    box-shadow: 0 -4px 16px rgba(0, 0, 0, 0.5);
    max-width: 800px;
    width: 90%;
    pointer-events: auto;
    animation: demo-slide-up 300ms ease-out;
    display: flex;
    flex-direction: column;
    gap: 8px;
    max-height: 35vh;
    overflow: hidden;
    z-index: 1000;
    border-radius: 8px;

    &--mobile {
      max-height: 45vh;
      padding: 8px 12px;
      max-width: 95%;
      bottom: 30px;
    }

    &--final-step {
      align-items: center;
      justify-content: center;
    }

    &--error {
      border-top-color: #ff3b30;
      box-shadow: 0 -4px 16px rgba(255, 59, 48, 0.4);
    }
  }

  &__step-wrapper {
    min-height: 80px;
    display: flex;
    flex-direction: column;
  }

  &__completion {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1001;
    animation: demo-fade-in 300ms ease-out;
  }

  &__completion-content {
    background-color: #1e1e2e;
    border-radius: 12px;
    padding: 40px;
    max-width: 400px;
    text-align: center;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.6);
    animation: demo-completion-enter 400ms cubic-bezier(0.4, 0, 0.2, 1);
  }

  &__completion-icon {
    font-size: 64px;
    margin-bottom: 16px;
    animation: demo-bounce 600ms ease-out;
  }

  &__completion-title {
    font-size: 28px;
    font-weight: 600;
    color: #e0e0e0;
    margin: 0 0 12px 0;
  }

  &__completion-message {
    font-size: 16px;
    color: #a0a0b0;
    line-height: 1.6;
    margin: 0 0 12px 0;
  }

  &__completion-stats {
    font-size: 13px;
    color: #888888;
    margin: 12px 0 20px 0;
  }

  &__completion-actions {
    display: flex;
    gap: 12px;
    flex-direction: column;
  }

  &__completion-btn {
    padding: 12px 24px;
    border: none;
    border-radius: 4px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 200ms ease-out;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    }

    &--finish {
      background-color: #007aff;
      color: #ffffff;

      &:hover {
        background-color: #0051d5;
      }
    }

    &--restart {
      background-color: #2a2a3e;
      color: #e0e0e0;

      &:hover {
        background-color: #3a3a52;
      }
    }
  }

  &__error {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 12px;
    background-color: #2a1a1a;
    border: 1px solid #5a2a2a;
    border-radius: 4px;
    margin-bottom: 12px;
  }

  &__error-icon {
    width: 20px;
    height: 20px;
    color: #ff3b30;
    flex-shrink: 0;
    margin-top: 2px;
  }

  &__error-content {
    flex: 1;
  }

  &__error-title {
    margin: 0 0 4px 0;
    font-size: 13px;
    font-weight: 600;
    color: #ff3b30;
  }

  &__error-message {
    margin: 0 0 8px 0;
    font-size: 12px;
    color: #ff6b6b;
    line-height: 1.5;
  }

  &__error-action {
    font-size: 12px;
    font-weight: 600;
    color: #007aff;
    background: none;
    border: none;
    cursor: pointer;
    padding: 0;

    &:hover {
      text-decoration: underline;
    }
  }

  &__error-close {
    width: 20px;
    height: 20px;
    background: none;
    border: none;
    color: #888888;
    cursor: pointer;
    font-size: 16px;
    padding: 0;
    flex-shrink: 0;

    &:hover {
      color: #333333;
    }
  }
}

@keyframes demo-slide-up {
  from {
    transform: translateY(100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

@keyframes demo-fade-in {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes demo-completion-enter {
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

@keyframes demo-bounce {
  0% {
    transform: scale(0);
  }
  50% {
    transform: scale(1.1);
  }
  100% {
    transform: scale(1);
  }
}
</style>
